import * as React from "react";
import { CredentialResponse } from "@react-oauth/google";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { useLanguage } from "../../contexts/LanguageContext"; // 🌍 Import Language Context
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import createAuthService from "../../utils/AuthService";
import { useAppContext } from "../../contexts/AppContext";

// Styled Card component
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow: theme.shadows[1],
  ...theme.applyStyles("dark", {
    boxShadow: theme.shadows[1],
  }),
}));

// Main Container
const SignUpContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(circle at top left, rgba(124, 58, 237, 0.18), transparent 32%), radial-gradient(circle at bottom right, rgba(79, 70, 229, 0.14), transparent 30%), linear-gradient(180deg, rgba(245, 243, 255, 0.78), rgba(255, 255, 255, 1))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(circle at top left, rgba(124, 58, 237, 0.28), transparent 28%), radial-gradient(circle at bottom right, rgba(79, 70, 229, 0.24), transparent 30%), linear-gradient(180deg, rgba(20, 16, 31, 1), rgba(28, 23, 48, 1))",
    }),
  },
}));;

export default function SignUp() {
  const { translations } = useLanguage(); // 🌍 Use translations
  const [data, setData] = React.useState({ firstName: "", email: "", password: "" });
  const [emailError, setEmailError] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState(false);
  const [nameError, setNameError] = React.useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = React.useState(false);
  const navigate = useNavigate();
  const { updateAppState } = useAppContext();
  const authService = createAuthService();

  const handleGoogleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    console.log(credentialResponse);

    if (!credentialResponse.credential) {
      console.error("Google login failed: No credential received.");
      return;
    }

    await authService.googleLoginAsync(credentialResponse.credential);
    navigate("/home");
  };
  const handleGoogleLoginError = () => {
    updateAppState({ error: "Google login failed" });
  };
  const handleSubmit = async () => {
    if (!data.firstName || !data.email || !data.password) {
      setNameError(!data.firstName);
      setEmailError(!data.email);
      setPasswordError(!data.password);
      return;
    }

    var response = await authService.signUpAsync(data);
    if (response.status > 201) return;
    setShowVerifyEmail(true);
  };

  return (
    <SignUpContainer direction="column" justifyContent="space-between">
      {!showVerifyEmail && (
        <Card variant="outlined">
          <Typography component="h1" variant="h4">
            {translations.auth.signup}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl>
              <FormLabel>{translations.auth.full_name}</FormLabel>
              <TextField
                required
                fullWidth
                id="name"
                error={nameError}
                onChange={(e) => setData({ ...data, firstName: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{translations.auth.email}</FormLabel>
              <TextField
                required
                fullWidth
                id="email"
                error={emailError}
                onChange={(e) => setData({ ...data, email: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{translations.auth.password}</FormLabel>
              <TextField
                required
                fullWidth
                type="password"
                id="password"
                error={passwordError}
                onChange={(e) => setData({ ...data, password: e.target.value })}
              />
            </FormControl>

            <Button fullWidth variant="contained" onClick={handleSubmit}>
              {translations.auth.signup}
            </Button>
          </Box>

          <Divider>or</Divider>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ alignItems: "center", justifyContent: "center", justifySelf: "center", alignSelf: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleLoginSuccess}
                onError={handleGoogleLoginError}
                theme="outline"
                logo_alignment="left"
                shape="rectangular"
                size="large"
              />
            </Box>

            <Typography sx={{ textAlign: "center" }}>
              {translations.auth.already_have_account}{" "}
              <Link href="/signin">{translations.auth.signin}</Link>
            </Typography>
          </Box>
        </Card>
      )}

      {showVerifyEmail && (
        <Box sx={{ alignItems: "center", textAlign: "center" }}>
          <Typography variant="h1">📧</Typography>
          <Typography variant="h5">{translations.auth.email_verification_title}</Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {translations.auth.email_verification_message}
          </Typography>
        </Box>
      )}
    </SignUpContainer>
  );
}
